import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";
import { useAuth } from "../../Contexts/AuthenticateProvider";

const NameGenderModal = ({ customer, onClose, onSaved }) => {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("customer.edit");
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [firstName, setFirstName] = useState(customer?.firstName || "");
    const [lastName, setLastName] = useState(customer?.lastName || "");
    const [gender, setGender] = useState(customer?.gender || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async () => {
        if (!canEdit) {
            toast.error("You don't have permission to edit this customer");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/customer/${customer.customerId}/name-gender`,
            data: {
                ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
                ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
                ...(gender ? { gender } : {}),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Updated successfully");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Edit Name & Gender</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {!canEdit ? (
                            <div className="alert alert-warning small mb-3">
                                You don't have permission to edit this customer's name or gender.
                            </div>
                        ) : null}
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">First name</label>
                                <input type="text" className="form-control" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={!canEdit} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Last name</label>
                                <input type="text" className="form-control" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={!canEdit} />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Gender</label>
                                <select className="form-select" value={gender} onChange={(e) => setGender(e.target.value)} disabled={!canEdit}>
                                    <option value="">Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="others">Others</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving || !canEdit}>{saving ? "Saving..." : "Save"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NameGenderModal;
